import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Exam, ExamAttempt, ExamQuestion } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';

function formatCountdown(totalSeconds: number) {
  const s = Math.max(0, totalSeconds);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function typeHint(type: ExamQuestion['question_type']) {
  if (type === 'multiple') return 'Select all that apply';
  if (type === 'true_false') return 'True or false';
  return 'Select one answer';
}

function answersKey(attemptId: number) {
  return `exam-answers-${attemptId}`;
}

function remainingFromExpiry(expiresAt: string | null | undefined) {
  if (!expiresAt) return null;
  return Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
}

export default function TakeExamPage() {
  const { id } = useParams<{ id: string }>();
  const examId = id ? parseInt(id, 10) : undefined;
  const { user } = useAuth();
  const navigate = useNavigate();

  const [exam, setExam] = useState<Exam | null>(null);
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null);
  const [answers, setAnswers] = useState<Record<number, number[]>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [timeUp, setTimeUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const submitLock = useRef(false);

  const isStudent = user?.role === 'student';
  const questions = exam?.questions || [];

  const initAnswers = useCallback((examData: Exam, attemptData: ExamAttempt) => {
    const initial: Record<number, number[]> = {};
    (examData.questions || []).forEach((q) => {
      if (q.id) initial[q.id] = [];
    });
    try {
      const raw = sessionStorage.getItem(answersKey(attemptData.id));
      if (raw) {
        const saved = JSON.parse(raw) as Record<number, number[]>;
        Object.keys(saved).forEach((k) => {
          initial[Number(k)] = saved[Number(k)] || [];
        });
      }
    } catch {
      /* ignore */
    }
    setAnswers(initial);
  }, []);

  useEffect(() => {
    if (!examId) return;
    (async () => {
      try {
        setLoading(true);
        const response = await apiClient.getExam(examId);
        if (response.success && response.data) {
          const data = response.data as Exam;
          setExam(data);
          if (isStudent && data.active_attempt) {
            setAttempt(data.active_attempt);
            initAnswers(data, data.active_attempt);
            setRemainingSeconds(remainingFromExpiry(data.active_attempt.expires_at));
          }
        } else {
          setError(response.error || 'Failed to load exam');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [examId, isStudent, initAnswers]);

  useEffect(() => {
    if (!attempt) return;
    sessionStorage.setItem(answersKey(attempt.id), JSON.stringify(answers));
  }, [answers, attempt]);

  const handleSubmit = useCallback(async (auto = false) => {
    if (!examId || !exam?.questions?.length || submitLock.current) return;
    const unanswered = exam.questions.filter((q) => q.id && !(answers[q.id] || []).length).length;
    if (!auto && unanswered > 0) {
      if (!confirm(`You have ${unanswered} unanswered question${unanswered === 1 ? '' : 's'}. Submit anyway?`)) {
        return;
      }
    }
    submitLock.current = true;
    setSubmitting(true);
    setError(null);
    const payload = exam.questions
      .filter((q) => q.id)
      .map((q) => ({
        question: q.id!,
        selected_options: answers[q.id!] || [],
      }));
    const response = await apiClient.submitExam(examId, payload, attempt?.id);
    if (response.success && response.data) {
      if (attempt) sessionStorage.removeItem(answersKey(attempt.id));
      setScore(Number(response.data.score));
    } else {
      setError(response.error || 'Failed to submit exam');
      submitLock.current = false;
    }
    setSubmitting(false);
  }, [answers, attempt, exam, examId]);

  useEffect(() => {
    if (!attempt?.expires_at || score !== null) return;
    const tick = () => {
      const left = remainingFromExpiry(attempt.expires_at);
      setRemainingSeconds(left);
      if (left !== null && left <= 0) {
        setTimeUp(true);
        handleSubmit(true);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [attempt, score, handleSubmit]);

  const beginAttempt = async () => {
    if (!examId) return;
    setStarting(true);
    setError(null);
    const res = await apiClient.startExam(examId);
    setStarting(false);
    if (res.success && res.data) {
      const next = res.data as ExamAttempt;
      setAttempt(next);
      if (exam) initAnswers(exam, next);
      setRemainingSeconds(remainingFromExpiry(next.expires_at));
    } else {
      setError(res.error || 'Could not start the exam.');
    }
  };

  const toggleOption = (question: ExamQuestion, optionId: number) => {
    if (!question.id) return;
    setAnswers((prev) => {
      const current = prev[question.id!] || [];
      if (question.question_type === 'multiple') {
        const next = current.includes(optionId)
          ? current.filter((oid) => oid !== optionId)
          : [...current, optionId];
        return { ...prev, [question.id!]: next };
      }
      return { ...prev, [question.id!]: [optionId] };
    });
  };

  const answeredCount = questions.filter((q) => q.id && (answers[q.id] || []).length > 0).length;
  const question = questions[currentIndex];
  const timerClass =
    remainingSeconds == null
      ? 'text-slate-600'
      : remainingSeconds <= 60
        ? 'text-red-600'
        : remainingSeconds <= 300
          ? 'text-amber-600'
          : 'text-slate-800';

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error && !exam) {
    return (
      <div className="p-8 text-center text-red-600">
        {error}
        <Link to="/exams" className="block mt-4 text-blue-600 underline">
          Back to exams
        </Link>
      </div>
    );
  }

  if (!exam) return null;

  if (score !== null) {
    const passMark = Number(exam.pass_mark ?? 50);
    const meritMark = Number(exam.merit_score ?? 80);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-100 p-6">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Exam submitted</p>
          <h1 className="mt-1 text-xl font-bold text-slate-900">{exam.title}</h1>
          <div className="mt-6 text-5xl font-bold text-blue-600">{score.toFixed(1)}%</div>
          <p className="mt-1 text-slate-500">Your score</p>
          {score >= meritMark ? (
            <p className="mt-4 text-sm text-emerald-700">
              You met the merit mark ({meritMark}%). Check Certificates after your lessons are complete.
            </p>
          ) : score >= passMark ? (
            <p className="mt-4 text-sm text-blue-700">
              You passed ({passMark}%). Complete all lessons to earn a Certificate of Completion.
            </p>
          ) : (
            <p className="mt-4 text-sm text-amber-700">
              You need at least {passMark}% to pass this exam for a certificate.
            </p>
          )}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            <Link to="/exams" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white">
              Back to exams
            </Link>
            <Link to="/certificates" className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-800">
              View certificates
            </Link>
            <button
              type="button"
              onClick={() => navigate('/progress')}
              className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-800"
            >
              View progress
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isStudent && !attempt) {
    const timed = !!exam.duration_minutes;
    return (
      <div className="mx-auto max-w-lg p-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Exam briefing</p>
        <h1 className="mt-1 text-2xl font-bold text-slate-900">{exam.title}</h1>
        {exam.description && <p className="mt-2 text-slate-600">{exam.description}</p>}
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-white px-3 py-2 shadow-sm">
            <dt className="text-xs text-slate-400">Questions</dt>
            <dd className="font-medium">{questions.length}</dd>
          </div>
          <div className="rounded-lg bg-white px-3 py-2 shadow-sm">
            <dt className="text-xs text-slate-400">Pass mark</dt>
            <dd className="font-medium">{Number(exam.pass_mark ?? 50)}%</dd>
          </div>
        </dl>
        {timed ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
            This exam is timed. You have {exam.duration_minutes} minutes. The timer starts when you confirm and cannot be paused.
            Unanswered questions are submitted as-is when time runs out.
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700">
            No time limit. You can take as long as you need.
          </div>
        )}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={beginAttempt}
            disabled={starting}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {starting ? 'Starting…' : 'Start exam'}
          </button>
          <Link to="/exams" className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">
            Cancel
          </Link>
        </div>
      </div>
    );
  }

  const session = isStudent && attempt;

  const body = (
    <>
      {timeUp && (
        <div className="bg-red-600 px-4 py-2 text-center text-sm font-medium text-white">
          Time is up — submitting your answers…
        </div>
      )}
      {error && (
        <div className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-56 shrink-0 overflow-y-auto border-r border-slate-200 bg-white p-4 lg:block">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Questions</p>
          <div className="mt-3 grid grid-cols-5 gap-1.5">
            {questions.map((q, index) => {
              const answered = q.id ? (answers[q.id] || []).length > 0 : false;
              const current = index === currentIndex;
              return (
                <button
                  key={q.id || index}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  className={`h-8 rounded-md text-xs font-semibold ${
                    current
                      ? 'bg-blue-600 text-white'
                      : answered
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                        : 'bg-slate-50 text-slate-600 ring-1 ring-slate-200'
                  }`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </aside>

        <div className="min-w-0 flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="mb-4 flex flex-wrap gap-1.5 lg:hidden">
            {questions.map((q, index) => {
              const answered = q.id ? (answers[q.id] || []).length > 0 : false;
              const current = index === currentIndex;
              return (
                <button
                  key={q.id || index}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  className={`h-8 w-8 rounded-md text-xs font-semibold ${
                    current
                      ? 'bg-blue-600 text-white'
                      : answered
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                        : 'bg-white text-slate-600 ring-1 ring-slate-200'
                  }`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>

          {question && (
            <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg font-semibold text-slate-900">
                  {currentIndex + 1}. {question.text}
                </h2>
                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {typeHint(question.question_type)}
                </span>
              </div>
              <div className="mt-5 space-y-2">
                {(question.options || []).map((option) => {
                  const optionId = option.id!;
                  const selected = question.id ? (answers[question.id] || []).includes(optionId) : false;
                  return (
                    <label
                      key={optionId}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
                        selected
                          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200'
                          : 'border-slate-200 hover:border-slate-300'
                      } ${!isStudent ? 'cursor-default' : ''}`}
                    >
                      <input
                        type={question.question_type === 'multiple' ? 'checkbox' : 'radio'}
                        name={`q-${question.id}`}
                        checked={selected}
                        disabled={!isStudent || submitting}
                        onChange={() => toggleOption(question, optionId)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm text-slate-800">{option.text}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mx-auto mt-6 flex max-w-2xl items-center justify-between">
            <button
              type="button"
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs text-slate-400">
              {currentIndex + 1} of {questions.length}
            </span>
            <button
              type="button"
              disabled={currentIndex >= questions.length - 1}
              onClick={() => setCurrentIndex((i) => Math.min(questions.length - 1, i + 1))}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const header = (
    <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-900">{exam.title}</p>
        <p className="text-xs text-slate-500">
          {answeredCount}/{questions.length} answered
        </p>
      </div>
      <div className={`font-mono text-lg font-bold tabular-nums ${timerClass}`}>
        {attempt?.expires_at
          ? formatCountdown(remainingSeconds ?? 0)
          : 'No time limit'}
      </div>
      {isStudent ? (
        <button
          type="button"
          onClick={() => handleSubmit(false)}
          disabled={submitting}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      ) : (
        <Link to="/exams" className="text-sm text-blue-600 hover:underline">
          Back
        </Link>
      )}
    </header>
  );

  if (session) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-slate-100">
        {header}
        {body}
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      {header}
      {body}
      {!isStudent && (
        <p className="pb-8 text-center text-sm text-slate-500">Preview mode — only students can submit answers.</p>
      )}
    </div>
  );
}
